import React from "react";
import PropTypes from "prop-types";
import { Typography, useTheme, Box, Skeleton, useMediaQuery } from "@mui/material";
import { Item } from "./Item";
import DashboardCardHeader from "./DashboardCardHeader";
import { HtmlTooltip } from "components/HeatMap";
import { InfoOutlined } from "@mui/icons-material";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";
import TrendingFlatRoundedIcon from "@mui/icons-material/TrendingFlatRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";

// Helper function to format the number based on type
const formatValue = (value, formatType) => {
  const numericValue = Number(value);
  if (isNaN(numericValue)) {
    return value;
  }

  if (formatType === "Currency") {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericValue);
  }

  if (formatType === "Percentage") {
    return new Intl.NumberFormat("en-US", {
      style: "percent",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericValue / 100);
  }

  // For "Number" or any other format, use standard numeric formatting
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numericValue);
};

const CardDashboardKPIChart = ({
  title,
  chart,
  thisMonth,
  lastMonth,
  percentage,
  formatType = "Number",
  showChart = true,
  height = 250,
  isLoading = false,
  currentPeriodName = "Current Period",
  lastMonthName = "Last Month", // New prop for the month name
  trending = null, // New prop for the trending icon
  reverse = false,
  tooltipTitle,
  tooltipContent,
  trendMode = "ratio",
}) => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");

  // On phones, cap fixed chart heights so chart cards don't dominate the viewport
  const h = isMobile ? (height > 300 ? 300 : Math.min(height, 200)) : height;

  const formattedThisMonth = formatValue(thisMonth, formatType);
  const formattedLastMonth = formatValue(lastMonth, formatType);

  const buildPercentageTooltip = () => {
    const previous = Number(lastMonth);
    const current = Number(thisMonth);

    if (!Number.isFinite(previous) || !Number.isFinite(current)) {
      return "This percentage compares the current period to the previous period.";
    }

    if (trendMode === "delta") {
      if (previous === 0) {
        if (current === 0) {
          return `${currentPeriodName}: 0\n${lastMonthName}: 0\nChange: 0\nPercentage change: 0%`;
        }

        return `${currentPeriodName}: ${formatValue(current, formatType)}\n${lastMonthName}: 0\nChange: +${formatValue(current, formatType)}\nPercentage change: 100% (shown by convention when previous period is zero)`;
      }

      const delta = current - previous;
      const changePercent = (delta / previous) * 100;
      const deltaPrefix = delta >= 0 ? "+" : "";
      const percentPrefix = changePercent >= 0 ? "+" : "";

      return `${currentPeriodName}: ${formatValue(current, formatType)}\n${lastMonthName}: ${formatValue(previous, formatType)}\nChange: ${deltaPrefix}${formatValue(delta, formatType)}\nPercentage change: ${percentPrefix}${changePercent.toFixed(2)}%`;
    }

    if (previous === 0) {
      return `${currentPeriodName}: ${formatValue(current, formatType)}\n${lastMonthName}: 0\nRatio comparison cannot be computed reliably when the previous period is zero`;
    }

    const ratioPercent = (current / previous) * 100;
    return `${currentPeriodName}: ${formatValue(current, formatType)}\n${lastMonthName}: ${formatValue(previous, formatType)}\nCurrent is ${ratioPercent.toFixed(2)}% of the previous period`;
  };

  // Get trend color based on percentage and reverse flag
  const getTrendColor = () => {
    const percentageNum = parseFloat(percentage);

    if (Number.isNaN(percentageNum)) {
      return theme.palette.text.secondary;
    }

    if (trendMode === "delta") {
      if (percentageNum === 0) return theme.palette.primary.main;

      const isPositiveTrend = reverse ? percentageNum < 0 : percentageNum > 0;

      return isPositiveTrend
        ? theme.palette.primary.main
        : theme.palette.error[600];
    }

    if (percentageNum === 100) return theme.palette.primary.main;

    const isPositiveTrend = reverse ? percentageNum < 100 : percentageNum > 100;

    return isPositiveTrend
      ? theme.palette.primary.main
      : theme.palette.error[600];
  };

  const getTrendingIcon = () => {
    const percentageNum = parseFloat(percentage);
    const trendColor = getTrendColor();

    if (Number.isNaN(percentageNum)) {
      return <TrendingFlatRoundedIcon sx={{ color: trendColor }} />;
    }

    if (trendMode === "delta") {
      if (percentageNum === 0) {
        return <TrendingFlatRoundedIcon sx={{ color: trendColor }} />;
      }

      const isPositiveTrend = reverse ? percentageNum < 0 : percentageNum > 0;

      return isPositiveTrend ? (
        <TrendingUpRoundedIcon sx={{ color: trendColor }} />
      ) : (
        <TrendingDownRoundedIcon sx={{ color: trendColor }} />
      );
    }

    if (percentageNum === 100)
      return <TrendingFlatRoundedIcon sx={{ color: trendColor }} />;

    const isPositiveTrend = percentageNum > 100;

    return isPositiveTrend ? (
      <TrendingUpRoundedIcon sx={{ color: trendColor }} />
    ) : (
      <TrendingDownRoundedIcon sx={{ color: trendColor }} />
    );
  };

  return (
    <Item
      sx={{
        p: 2.5,
        position: "relative",
        bgcolor: theme.palette.background.default,
        border: `1px solid ${theme.palette.primary.border2}`,
        borderRadius: "12px",
        boxShadow: theme.shadows[2],
      }}
    >
      {/* Trending icon positioned at the top right */}
      {trending && !isLoading && (
        <Box
          sx={{
            position: "absolute",
            top: theme.spacing(2),
            right: theme.spacing(2),
            zIndex: 1,
          }}
        >
          {getTrendingIcon()}
        </Box>
      )}
      <Box display="flex" flexDirection="column" width="100%">
        <Box mb={1}>
          <DashboardCardHeader
            title={title}
            tooltipTitle={tooltipTitle}
            tooltipContent={tooltipContent}
            titleVariant="h6"
            titleColor={theme.palette.text.primary}
          />
        </Box>

        {isLoading ? (
          <>
            <Box mb={1} display="flex" justifyContent="center">
              <Skeleton
                variant="text"
                width="60%"
                height={60}
                animation="wave"
                sx={{ backgroundColor: theme.palette.background.default }}
              />
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Skeleton
                variant="text"
                width="30%"
                height={24}
                animation="wave"
                sx={{ backgroundColor: theme.palette.background.default }}
              />
              <Skeleton
                variant="text"
                width="20%"
                height={24}
                animation="wave"
                sx={{ backgroundColor: theme.palette.background.default }}
              />
            </Box>

            {showChart && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height={h || "200px"}
                width="100%"
              >
                <Box
                  height="200px"
                  width="200px"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Skeleton
                    variant="circular"
                    width="100%"
                    height="100%"
                    animation="wave"
                    sx={{ backgroundColor: theme.palette.background.default }}
                  />
                </Box>
              </Box>
            )}
          </>
        ) : (
          <>
            {thisMonth ? (
              <Box mb={1} display="flex" justifyContent="center">
                <Typography
                  variant="h3"
                  align="center"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                  }}
                >
                  {formattedThisMonth}
                </Typography>
              </Box>
            ) : null}

            {lastMonth && percentage ? (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.text.secondary,
                  }}
                >
                  {lastMonthName}: {formattedLastMonth}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      color: getTrendColor(),
                    }}
                  >
                    {percentage}
                  </Typography>
                  <HtmlTooltip
                    title={
                      <Box sx={{ p: 1, maxWidth: 380 }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          How this percentage is calculated
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                          {buildPercentageTooltip()}
                        </Typography>
                      </Box>
                    }
                    arrow
                    placement="left"
                  >
                    <InfoOutlined
                      sx={{
                        fontSize: 16,
                        color: theme.palette.text.secondary,
                        cursor: "help",
                        opacity: 0.8,
                        "&:hover": { opacity: 1 },
                      }}
                    />
                  </HtmlTooltip>
                </Box>
              </Box>
            ) : null}

            {showChart ? (
              <Box sx={{ height: h, width: "100%" }}>{chart}</Box>
            ) : null}
          </>
        )}
      </Box>
    </Item>
  );
};

CardDashboardKPIChart.propTypes = {
  title: PropTypes.string.isRequired,
  thisMonth: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  lastMonth: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  percentage: PropTypes.string.isRequired,
  formatType: PropTypes.oneOf(["Number", "Currency", "Percentage"]),
  chart: PropTypes.node.isRequired,
  showChart: PropTypes.bool,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isLoading: PropTypes.bool,
  currentPeriodName: PropTypes.string,
  lastMonthName: PropTypes.string, // Added prop type for the month name
  trending: PropTypes.node, // Added prop type for the trending icon
  reverse: PropTypes.bool,
  tooltipTitle: PropTypes.string,
  tooltipContent: PropTypes.string,
  trendMode: PropTypes.oneOf(["ratio", "delta"]),
};

export default CardDashboardKPIChart;
